import type {
  SparkChatDebugInfo,
  SparkChatMessage,
  SparkChatOutcome,
} from "../types";
import { debugOf, outcomeOf, sourcesOf, textOf } from "../types";

// What the test panel says about a conversation: the token count and the
// transcript it copies. Kept free of React so both follow from the messages alone.

const count = (n: number) => n.toLocaleString("de-DE");

/** Messages the tester has sent in this conversation, answered or not. */
export const describeSent = (messages: SparkChatMessage[]) =>
  `${count(messages.filter((m) => m.role === "user").length)} gesendet`;

export type TokenTotals = {
  input: number;
  output: number;
  /** Turns that reached the model — the others cost no tokens. */
  modelCalls: number;
};

/**
 * Tokens of the chat model, as the API reports them per turn. The moderation
 * calls around it are not in here: the API does not count them.
 */
export const tokenTotalsOf = (messages: SparkChatMessage[]): TokenTotals => {
  const totals = { input: 0, output: 0, modelCalls: 0 };
  for (const message of messages) {
    const debug = debugOf(message);
    if (debug?.inputTokens === undefined) continue;
    totals.input += debug.inputTokens;
    totals.output += debug.outputTokens ?? 0;
    totals.modelCalls += 1;
  }
  return totals;
};

export const describeTokens = ({ input, output, modelCalls }: TokenTotals) =>
  `${count(input)} Eingabe + ${count(output)} Ausgabe = ${count(input + output)} (${count(modelCalls)} ${modelCalls === 1 ? "Modellaufruf" : "Modellaufrufe"})`;

const FIXED_REPLY: Record<SparkChatOutcome, string> = {
  // "answered" with a fixed text is the decline of an off-topic question.
  answered: "Off-Topic-Absage",
  crisis: "Krisenantwort",
  identity: "KI-Hinweis",
  blocked_local: "Blockiert",
  blocked_input: "Blockiert",
  blocked_output: "Blockiert",
  moderation_failed: "Moderation nicht erreichbar",
};

const DECIDED_BY: Record<SparkChatDebugInfo["decidedBy"], string> = {
  local_check: "lokale Prüfung, ohne Modell",
  input_moderation: "Moderation der Nachricht",
  model: "Modell",
  output_moderation: "Moderation der Modellantwort",
};

/** "generiert" or which fixed reply it is, e.g. "feste Antwort: Krisenantwort (crisis)". */
export const describeReply = (message: SparkChatMessage) => {
  const debug = debugOf(message);
  if (!debug) return "keine Metadaten";
  if (debug.generated) return "generiert";

  const outcome = outcomeOf(message) ?? "answered";
  return `feste Antwort: ${FIXED_REPLY[outcome]} (${outcome})`;
};

/** What set the turn on its path; `userNo` is the number of the message that was answered. */
export const describeTrigger = (debug: SparkChatDebugInfo, userNo: number) => {
  const categories =
    debug.categories.length > 0 ? `: ${debug.categories.join(", ")}` : "";

  if (debug.decidedBy === "output_moderation") {
    return `Auslöser: die Modellantwort auf Nachricht #${userNo}, nicht ausgeliefert (${DECIDED_BY.output_moderation}${categories})`;
  }
  if (debug.decidedBy === "model") {
    return debug.generated
      ? `Antwort des Modells auf Nachricht #${userNo}`
      : `Auslöser: Nachricht #${userNo} (Modell meldet: nicht im Material)`;
  }
  return `Auslöser: Nachricht #${userNo} (${DECIDED_BY[debug.decidedBy]}${categories})`;
};

const describeTurn = (message: SparkChatMessage, userNo: number) => {
  const debug = debugOf(message);
  if (!debug) return describeReply(message);

  const sources = sourcesOf(message);
  return [
    describeReply(message),
    describeTrigger(debug, userNo),
    debug.inputTokens === undefined
      ? "Tokens: keine"
      : `Tokens: ${count(debug.inputTokens)} Eingabe / ${count(debug.outputTokens ?? 0)} Ausgabe`,
    `${count(debug.durationMs)} ms`,
    sources.length > 0 &&
      `Quellen: ${sources.map((s) => `${s.title} (${s.id})`).join(", ")}`,
    debug.piiRedacted && "persönliche Daten vor dem Modell entfernt",
  ]
    .filter(Boolean)
    .join(" · ");
};

/** The conversation as plain text, every answer with how it came about. */
export const buildTranscript = ({
  sparkTitle,
  chatId,
  messages,
  copiedAt,
}: {
  sparkTitle: string;
  chatId: string;
  messages: SparkChatMessage[];
  copiedAt: Date;
}) => {
  const model = messages.map(debugOf).find(Boolean)?.model;
  const head = [
    "KI-Assistent – Testprotokoll",
    `Spark: ${sparkTitle}`,
    `Chat: ${chatId}`,
    `Kopiert: ${copiedAt.toLocaleString("de-DE")}`,
    model && `Modell: ${model}`,
    `Nachrichten: ${describeSent(messages)}`,
    `Tokens: ${describeTokens(tokenTotalsOf(messages))}`,
  ].filter(Boolean);

  let userNo = 0;
  const turns = messages.map((message, index) => {
    const no = index + 1;
    if (message.role === "user") {
      userNo = no;
      return `#${no} Du\n${textOf(message)}`;
    }
    return `#${no} KI-Assistent\n[${describeTurn(message, userNo)}]\n${textOf(message)}`;
  });

  return [head.join("\n"), ...turns].join("\n\n");
};
